/**
 * F&B 입점 업체 상세 페이지
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Spinner, Row, Col, Alert } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { fnbService } from '../../services/fnbService';
import { FnbVendor } from '../../types/fnb';
import { format } from 'date-fns';

const FnbVendorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const vendorId = id ? parseInt(id, 10) : 0;
  const isNew = id === 'new';

  const { data: vendor, isLoading } = useQuery({
    queryKey: ['fnbVendor', vendorId],
    queryFn: () => fnbService.getVendor(vendorId),
    enabled: !!vendorId && vendorId > 0 && !isNew,
  });

  const deleteMutation = useMutation({
    mutationFn: () => fnbService.deleteVendor(vendorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fnbVendors'] });
      navigate('/admin/fnb/vendors');
    },
    onError: (error: any) => {
      alert(`삭제 실패: ${error.message}`);
    },
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  const handleDelete = () => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      deleteMutation.mutate();
    }
  };

  if (isNew) {
    navigate('/admin/fnb/vendors/new');
    return null;
  }

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!vendor) {
    return (
      <AdminLayout>
        <Card>
          <Card.Body>
            <p>F&B 업체를 찾을 수 없습니다.</p>
            <Button variant="secondary" onClick={() => navigate('/admin/fnb/vendors')}>
              목록으로
            </Button>
          </Card.Body>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>F&B 입점 업체 상세</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/fnb/vendors')} className="me-2">
              목록으로
            </Button>
            <Button variant="primary" onClick={() => navigate(`/admin/fnb/vendors/${vendorId}/edit`)} className="me-2">
              수정
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              삭제
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <h3>{vendor.vendor_name}</h3>
                  <div className="mt-2">
                    {getStatusBadge(vendor.status)}
                  </div>
                </div>

                <div className="mb-3">
                  <strong>업체 코드:</strong> 
                  <span style={{ color: '#ccc', marginLeft: '10px' }}>{vendor.vendor_code}</span>
                </div>

                {vendor.vendor_type && (
                  <div className="mb-3">
                    <strong>업체 유형:</strong> 
                    <span style={{ color: '#ccc', marginLeft: '10px' }}>{vendor.vendor_type}</span>
                  </div>
                )}

                {vendor.location && (
                  <div className="mb-3">
                    <strong>위치:</strong> 
                    <span style={{ color: '#ccc', marginLeft: '10px' }}>{vendor.location}</span>
                  </div>
                )}

                <div className="mb-3">
                  <strong>수수료율:</strong> 
                  <span style={{ color: '#00BCD4', fontWeight: 'bold', marginLeft: '10px' }}>
                    {vendor.commission_rate}%
                  </span>
                </div>
              </Col>

              <Col md={6}>
                {vendor.contact_name && (
                  <div className="mb-3">
                    <strong>담당자:</strong> 
                    <span style={{ color: '#ccc', marginLeft: '10px' }}>{vendor.contact_name}</span>
                  </div>
                )}

                {vendor.email && (
                  <div className="mb-3">
                    <strong>이메일:</strong> 
                    <span style={{ color: '#ccc', marginLeft: '10px' }}>{vendor.email}</span>
                  </div>
                )}

                {vendor.phone && (
                  <div className="mb-3">
                    <strong>연락처:</strong> 
                    <span style={{ color: '#ccc', marginLeft: '10px' }}>{vendor.phone}</span>
                  </div>
                )}

                {vendor.contract_start && vendor.contract_end && (
                  <div className="mb-3">
                    <strong>계약 기간:</strong> 
                    <span style={{ color: '#ccc', marginLeft: '10px' }}>
                      {format(new Date(vendor.contract_start), 'yyyy-MM-dd')} ~ {format(new Date(vendor.contract_end), 'yyyy-MM-dd')}
                    </span>
                  </div>
                )}

                <div className="mb-3">
                  <strong>등록일:</strong> 
                  <span style={{ color: '#ccc', marginLeft: '10px' }}>
                    {format(new Date(vendor.created_at), 'yyyy-MM-dd HH:mm:ss')}
                  </span>
                </div>

                {vendor.updated_at && (
                  <div className="mb-3">
                    <strong>수정일:</strong> 
                    <span style={{ color: '#ccc', marginLeft: '10px' }}>
                      {format(new Date(vendor.updated_at), 'yyyy-MM-dd HH:mm:ss')}
                    </span>
                  </div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>매출 현황</h5>
              <Button variant="info" size="sm" onClick={() => navigate(`/admin/fnb/vendors/${vendorId}/sales`)}>
                매출 상세 보기
              </Button>
            </div>
            <Alert variant="info">
              매출 데이터는 POS 연동 또는 수동 입력을 통해 관리됩니다.
            </Alert>
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default FnbVendorDetail;

